import {useCallback} from 'react';
import {signInWithCustomToken} from 'firebase/auth';
import {auth} from '@src/firebase';
import {useLazyGetMemberByUsernameQuery} from '@src/Api/TraceApi/traceApi';
import {MtnAllowLoginResponse} from '@src/Api/TraceApi/endpoints/mtn.endpoint';
import {useSession} from '@src/Providers/SessionProvider/SessionProvider';
import {SESSION_ID_KEY} from '@src/Providers/SessionProvider/useSessionId';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import useTracker from '@src/Services/Amplitude/hooks/useTracker';
import {trackTealiumLoginSuccessful} from '@src/Tracking/tealiumEvents';
import {MTN_TEST_LOGIN_KEY, isMtnSite} from '@src/Configs/mtnConfig';
import {AppDispatch, useAppDispatch} from '@src/Store/hooks';
import memberSlice from '@src/Store/Slices/Member.slice';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import apiConfig from '@src/Configs/apiConfig';
import {Member} from '@src/Api/Umbraco/interface/Member.interface';

export interface MtnTestLogin {
  mtnToken: string;
  userId: string;
  subscriptionStatus: MtnAllowLoginResponse['subscriptionStatus'];
  contentAccessEndDate: string;
  loggedInAt: string;
}

/** Member ids of test logins start with this, so nothing asks the backend for them. */
export const MTN_TEST_MEMBER_PREFIX = 'mtn-nodb:';

export const isMtnTestMember = (memberId?: string) =>
  !!memberId && memberId.startsWith(MTN_TEST_MEMBER_PREFIX);

/**
 * Puts a test login into the store like a real one: member + MTN subscription.
 * Profile → My membership then shows the MTN plan card.
 */
export const applyMtnTestLogin = (
  dispatch: AppDispatch,
  testLogin: MtnTestLogin,
) => {
  const memberId = `${MTN_TEST_MEMBER_PREFIX}${testLogin.userId}`;
  const isActive =
    testLogin.subscriptionStatus === 'ACTIVE' &&
    new Date(testLogin.contentAccessEndDate) > new Date();
  dispatch(
    memberSlice.actions.setMember({
      _id: memberId,
      email: `${testLogin.userId}@mtn.local`,
      username: `${testLogin.userId}@mtn.local`,
      name: 'MTN',
      firstName: 'MTN',
      lastName: 'User',
      language: ['en-US'],
    } as unknown as Member),
  );
  dispatch(
    memberSlice.actions.setMemberSubscription({
      id: memberId,
      memberId,
      subscriptionStart: testLogin.loggedInAt,
      subscriptionEnd: testLogin.contentAccessEndDate,
      productId: isActive
        ? 'trace.monthly.subscription'
        : apiConfig.freemiumProductId,
      carrier: 'mtn',
    }),
  );
};

export const readMtnTestLogin = (): MtnTestLogin | null => {
  // Never active on other domains (trace.plus, vc.trace.plus)
  if (!isMtnSite()) return null;
  try {
    const raw = localStorage.getItem(MTN_TEST_LOGIN_KEY);
    return raw ? (JSON.parse(raw) as MtnTestLogin) : null;
  } catch {
    return null;
  }
};

/** Fired when the MTN test login starts or ends, for code outside AuthProvider. */
export const MTN_TEST_LOGIN_EVENT = 'mtn-test-login-changed';

const MTN_TEST_LOGIN_MAX_AGE_MS = 24 * 60 * 60 * 1000; // same as the mtnToken

const notifyMtnTestLogin = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(MTN_TEST_LOGIN_EVENT));
  }
};

/** True while a stored MTN test login is still valid (24h). */
export const hasActiveMtnTestLogin = (): boolean => {
  if (typeof window === 'undefined') return false;
  const testLogin = readMtnTestLogin();
  if (!testLogin?.loggedInAt) return false;
  return (
    Date.now() - new Date(testLogin.loggedInAt).getTime() <
    MTN_TEST_LOGIN_MAX_AGE_MS
  );
};

export const clearMtnTestLogin = () => {
  try {
    localStorage.removeItem(MTN_TEST_LOGIN_KEY);
  } catch {
    /* noop */
  }
  notifyMtnTestLogin();
};

/**
 * Finishes an MTN `allow_login` response.
 *
 * - Normal: Firebase sign-in with the custom token, session, member fetch.
 * - Testing (backend MTN_DB_DISABLED, response has `mtnToken`): no Firebase
 *   sign-in, because that would create the member/session in the database.
 *   The user is logged in locally (store + localStorage) and the mtnToken is
 *   kept for refresh/unsubscribe.
 *
 * Returns 'test' or 'firebase' so the caller can show the right screen.
 */
export function useMtnLoginCompletion() {
  const [fetchMember] = useLazyGetMemberByUsernameQuery();
  const session = useSession();
  const {setIsLoggedIn, setSessionManuallyInitialized} = useIsLoggedIn();
  const tracker = useTracker();
  const dispatch = useAppDispatch();

  return useCallback(
    async (response: MtnAllowLoginResponse): Promise<'test' | 'firebase'> => {
      if (response.mtnToken) {
        const testLogin: MtnTestLogin = {
          mtnToken: response.mtnToken,
          userId: response.session?.userId || 'carrier:mtn:test',
          subscriptionStatus: response.subscriptionStatus,
          contentAccessEndDate: response.contentAccessEndDate,
          loggedInAt: new Date().toISOString(),
        };
        try {
          localStorage.setItem(MTN_TEST_LOGIN_KEY, JSON.stringify(testLogin));
        } catch {
          /* noop */
        }
        applyMtnTestLogin(dispatch, testLogin);
        notifyMtnTestLogin();
        setIsLoggedIn(true);
        dispatch(appAuthSlice.actions.closeAuthModal());
        return 'test';
      }

      try {
        setSessionManuallyInitialized(true);
        const userCredential = await signInWithCustomToken(
          auth,
          response.accessToken,
        );
        if (!userCredential.user) throw new Error('Firebase auth failed');
        await userCredential.user.getIdToken(true);

        if (response.session?._id) {
          localStorage.setItem(SESSION_ID_KEY, response.session._id);
        } else {
          await session.initializeSession();
        }

        await fetchMember();
        tracker.general.loggedIn({account_type: 'mtn'});
        trackTealiumLoginSuccessful({accountType: 'mtn'});
        setIsLoggedIn(true);
        return 'firebase';
      } catch (err) {
        try {
          await auth.signOut();
        } catch {
          /* noop */
        }
        throw err;
      } finally {
        setSessionManuallyInitialized(false);
      }
    },
    [
      fetchMember,
      session,
      setIsLoggedIn,
      setSessionManuallyInitialized,
      tracker,
      dispatch,
    ],
  );
}

export default useMtnLoginCompletion;
